/**
 * ORION "How would I look?" – virtueel passen op de productpagina.
 *
 * - Leest het product en de gekozen variant van de pagina (Shopify blijft de bron; de server controleert alles opnieuw).
 * - Foto wordt in de browser verkleind en als JPEG opnieuw opgeslagen (geen EXIF/locatie mee), pas ná toestemming geüpload.
 * - Praat alleen met de ORION-middleware (geen AI-sleutels in de browser).
 * - Analytics: Shopify.analytics.publish + anonieme tellers bij de middleware (nooit de foto).
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const LANG = (document.documentElement.lang || 'nl').slice(0, 2);

const T = {
  nl: {
    title: 'How would I look?',
    subtitle: 'See yourself in ORION.',
    close: 'Sluiten',
    uploadTitle: 'Upload je foto',
    uploadText: 'Gebruik een duidelijke foto van je hele lichaam of je bovenlichaam. Sta ontspannen en zorg dat je lichaam en kleding goed zichtbaar zijn.',
    choose: 'Kies een foto',
    chooseHint: 'JPG, PNG of WEBP · camera of fotobibliotheek',
    tips: ['Goed licht, recht van voren', 'Alleen jij op de foto', 'Voor een complete set: van top tot teen'],
    yourPhoto: 'Jouw foto',
    selected: 'Jouw ORION-keuze',
    pickColor: 'Kies je kleur',
    pickPiece: 'Kies je stuk',
    changePhoto: 'Andere foto',
    privacy:
      'Je foto wordt alleen gebruikt om jouw ORION-look te maken en wordt automatisch verwijderd volgens ons bewaarbeleid (foto na 1 uur, look na 24 uur). We gebruiken je foto niet om AI te trainen.',
    privacyLink: 'Privacybeleid',
    consent: 'Ik ga ermee akkoord dat mijn foto wordt verwerkt om mijn AI-look te maken.',
    generate: 'Generate my ORION look',
    loading: ['Je ORION-look wordt voorbereid…', 'Je gekozen stukken worden gepast…', 'Jouw uiterlijk blijft behouden…', 'Je look wordt gemaakt…', 'Bijna klaar…'],
    waitNote: 'Dit duurt meestal 30 tot 60 seconden. Je kunt dit venster open laten.',
    progress: 'Voortgang van je look',
    queue: (n) => `Nog ${n} ${n === 1 ? 'look' : 'looks'} voor je in de rij`,
    resultTitle: 'Your ORION look',
    visualNote: 'Een AI-visualisatie: pasvorm en kleur kunnen in het echt iets afwijken.',
    size: 'Kies je maat',
    addToCart: 'In winkelwagen',
    adding: 'Toevoegen…',
    added: 'Toegevoegd',
    soldOut: 'Uitverkocht',
    anotherColor: 'Probeer een andere kleur',
    prefetching: 'We maken de andere kleuren alvast voor je klaar.',
    prefetchReady: 'Kleuren met ✓ staan direct klaar.',
    anotherPiece: 'Probeer een ander ORION-stuk',
    again: 'Opnieuw genereren',
    download: 'Bewaar je look',
    share: 'Delen',
    deleteData: 'Verwijder mijn foto nu',
    deleted: 'Je foto en looks zijn verwijderd.',
    retry: 'Opnieuw proberen',
    braOnly: 'De sport-bh los kunnen we op dit moment niet altijd tonen. Bekijk hem in de complete set of met de wrap bolero.',
    trySet: 'Probeer de complete set',
    otherPhoto: 'Andere foto kiezen',
    errors: {
      NO_PERSON: 'We konden je niet goed zien op deze foto. Probeer een foto waarop je lichaam beter zichtbaar is.',
      MULTIPLE_PEOPLE: 'Er staat meer dan één persoon op deze foto. Kies een foto waarop alleen jij staat.',
      LOWER_BODY_NOT_VISIBLE: 'Voor dit stuk moeten we je benen kunnen zien. Probeer een foto van top tot teen.',
      PHOTO_NOT_USABLE: 'Deze foto kunnen we niet gebruiken. Probeer een JPG-, PNG- of WEBP-foto tot 12 MB.',
      GENERATION_FAILED: 'Het is ons deze keer niet gelukt om je ORION-look te maken. Probeer het nog eens.',
      RATE_LIMITED: 'Je hebt al een paar looks gemaakt. Wacht even voordat je een nieuwe maakt.',
      PRODUCT_ERROR: 'We konden dit ORION-stuk niet laden. Vernieuw de pagina en probeer het opnieuw.',
      PRODUCT_NOT_SUPPORTED: 'Dit stuk kun je nog niet virtueel passen.',
      SERVICE_UNAVAILABLE: 'How would I look? neemt even pauze. Probeer het later nog eens.',
      CONTENT_REJECTED: 'Van deze foto kunnen we geen look maken. Probeer een andere foto.',
      PHOTO_EXPIRED: 'Je foto is voor je privacy verwijderd. Upload hem opnieuw om verder te gaan.',
    },
  },
  en: {
    title: 'How would I look?',
    subtitle: 'See yourself in ORION.',
    close: 'Close',
    uploadTitle: 'Upload your photo',
    uploadText: 'Upload a clear full-body or upper-body photo. For the best result, stand naturally and make sure your outfit and body are clearly visible.',
    choose: 'Choose a photo',
    chooseHint: 'JPG, PNG or WEBP · camera or photo library',
    tips: ['Good light, facing the camera', 'Only you in the photo', 'For a complete set: head to toe'],
    yourPhoto: 'Your photo',
    selected: 'Your ORION selection',
    pickColor: 'Choose your color',
    pickPiece: 'Choose your piece',
    changePhoto: 'Change photo',
    privacy:
      'Your photo is used only to generate your ORION look and is automatically deleted after processing according to our retention policy (photo after 1 hour, look after 24 hours). We never use your photo to train AI.',
    privacyLink: 'Privacy policy',
    consent: 'I agree to my photo being processed to create my AI look.',
    generate: 'Generate my ORION look',
    loading: ['Preparing your ORION look…', 'Fitting your selected pieces…', 'Preserving your identity…', 'Creating your look…', 'Almost ready…'],
    waitNote: 'This usually takes 30 to 60 seconds. You can keep this window open.',
    progress: 'Progress of your look',
    queue: (n) => `${n} ${n === 1 ? 'look' : 'looks'} ahead of you`,
    resultTitle: 'Your ORION look',
    visualNote: 'An AI visualization: fit and colour may differ slightly in real life.',
    size: 'Select your size',
    addToCart: 'Add to cart',
    adding: 'Adding…',
    added: 'Added',
    soldOut: 'Sold out',
    anotherColor: 'Try another color',
    prefetching: "We're preparing the other colors for you.",
    prefetchReady: 'Colors with ✓ are ready instantly.',
    anotherPiece: 'Try another ORION product',
    again: 'Generate again',
    download: 'Save look',
    share: 'Share look',
    deleteData: 'Delete my photo now',
    deleted: 'Your photo and looks have been deleted.',
    retry: 'Try again',
    braOnly: "We can't always show the sports bra on its own right now. See it in the complete set or with the wrap bolero.",
    trySet: 'Try the complete set',
    otherPhoto: 'Choose another photo',
    errors: {
      NO_PERSON: "We couldn't clearly see you in this photo. Try another photo with your body more visible.",
      MULTIPLE_PEOPLE: 'There is more than one person in this photo. Choose a photo with just you.',
      LOWER_BODY_NOT_VISIBLE: 'For this piece we need to see your legs. Try a head-to-toe photo.',
      PHOTO_NOT_USABLE: "This photo can't be used. Try a JPG, PNG or WEBP photo up to 12 MB.",
      GENERATION_FAILED: "We couldn't create your ORION look this time. Please try again.",
      RATE_LIMITED: "You've created several looks already. Please wait a moment before generating another.",
      PRODUCT_ERROR: "We couldn't load this ORION item. Refresh the page and try again.",
      PRODUCT_NOT_SUPPORTED: "This piece can't be tried on yet.",
      SERVICE_UNAVAILABLE: 'How would I look? is taking a short break. Please try again later.',
      CONTENT_REJECTED: "We can't create a look from this photo. Try another photo.",
      PHOTO_EXPIRED: 'Your photo was removed for your privacy. Upload it again to continue.',
    },
  },
};
const t = T[LANG] || T.en;

/** Servercodes naar klantteksten (nooit ruwe fouten tonen). */
const ERROR_MAP = {
  NO_PERSON: 'NO_PERSON',
  MULTIPLE_PEOPLE: 'MULTIPLE_PEOPLE',
  LOWER_BODY_NOT_VISIBLE: 'LOWER_BODY_NOT_VISIBLE',
  IMAGE_TOO_SMALL: 'PHOTO_NOT_USABLE',
  IMAGE_TOO_LARGE: 'PHOTO_NOT_USABLE',
  UNREADABLE_IMAGE: 'PHOTO_NOT_USABLE',
  PHOTO_NOT_USABLE: 'PHOTO_NOT_USABLE',
  UNSUPPORTED_FILE: 'PHOTO_NOT_USABLE',
  FILE_TOO_LARGE: 'PHOTO_NOT_USABLE',
  RATE_LIMITED: 'RATE_LIMITED',
  DAILY_LIMIT: 'RATE_LIMITED',
  BUSY: 'SERVICE_UNAVAILABLE',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  PRODUCT_ERROR: 'PRODUCT_ERROR',
  PRODUCT_NOT_FOUND: 'PRODUCT_ERROR',
  PRODUCT_NOT_SUPPORTED: 'PRODUCT_NOT_SUPPORTED',
  CONTENT_REJECTED: 'CONTENT_REJECTED',
  PHOTO_EXPIRED: 'PHOTO_EXPIRED',
  SESSION_EXPIRED: 'PHOTO_EXPIRED',
  EXPIRED: 'PHOTO_EXPIRED',
  POLICY_CHANGED: 'SERVICE_UNAVAILABLE',
};

const STORE_KEY = 'orion-tryon-state';
const COLOR_HEX = { zwart: '#111111', zand: '#c9b8a3', mint: '#b7cfbd', roze: '#e8b9b5', wit: '#f4f2ee', ivoor: '#efe9df' };
// optienamen kunnen per taal vertaald zijn
const isColor = (n) => /^(kleur|colou?r|farbe|couleur)$/i.test(n || '');
const isSize = (n) => /^(maat|size|größe|grösse|taille|talla)$/i.test(n || '');
const isPiece = (n) => /^(uitvoering|style|piece|ausführung|modèle|estilo|version)$/i.test(n || '');

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const money = (cents) =>
  new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' })
    .format(cents / 100)
    .replace(/^([€$£])\s/, '$1'); // zoals de winkel: €149,99

function track(endpoint, event, data = {}) {
  try {
    window.Shopify?.analytics?.publish?.(event, data);
  } catch {
    /* geen pixel actief */
  }
  try {
    fetch(`${endpoint}/api/tryon/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* tellen is nooit belangrijker dan de klant */
  }
}

/** Foto in de browser verkleinen + als JPEG opnieuw opslaan: draait de foto recht en laat EXIF/GPS achter. */
async function prepareFile(file) {
  if (!/^image\//.test(file.type) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)) throw new Error('PHOTO_NOT_USABLE');
  if (file.size > 40 * 1024 * 1024) throw new Error('PHOTO_NOT_USABLE');
  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new Error('PHOTO_NOT_USABLE');
  }
  const max = 2048;
  const s = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * s);
  const h = Math.round(bitmap.height * s);
  if (Math.min(w, h) < 400) throw new Error('PHOTO_NOT_USABLE');
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', 0.9));
  if (!blob) throw new Error('PHOTO_NOT_USABLE');
  return blob;
}

class OrionTryOn extends HTMLElement {
  #state = { step: 'upload', photo: null, photoUrl: null, consent: false, session: null, uploadId: null, generationId: null, variantId: null, size: null, busy: false };
  #poll = 0;
  #msgTimer = 0;
  #barTimer = 0;
  #preTimer = 0;
  #upload = null;
  #pre = { upload: null, parent: null, ready: new Set(), seen: new Set(), busy: false, asked: false };
  #prog = { shown: 0, stage: 'queued', stageAt: 0 };

  connectedCallback() {
    this.endpoint = (this.dataset.endpoint || '').replace(/\/$/, '');
    this.policy = this.dataset.policy || '';
    this.privacyUrl = this.dataset.privacyUrl || '/policies/privacy-policy';
    try {
      this.product = JSON.parse(this.querySelector('[data-tryon-product]')?.textContent || '{}');
    } catch {
      this.product = null;
    }
    if (!this.endpoint || !this.product?.variants?.length) return;
    // voorbeeldmodus: knop alleen zichtbaar met ?tryon=preview (wordt onthouden in deze browser)
    if (this.dataset.mode === 'preview') {
      try {
        if (new URLSearchParams(location.search).get('tryon') === 'preview') localStorage.setItem('orion-tryon-preview', '1');
        if (new URLSearchParams(location.search).get('tryon') === 'off') localStorage.removeItem('orion-tryon-preview');
        this.hidden = localStorage.getItem('orion-tryon-preview') !== '1';
      } catch {
        this.hidden = true;
      }
    } else {
      this.hidden = false;
    }
    this.querySelector('[data-tryon-open]')?.addEventListener('click', () => this.open());
    // menulink "How would I look?" -> /products/...?tryon=open opent de modal direct
    const params = new URLSearchParams(location.search);
    if (!this.hidden && params.get('tryon') === 'open') {
      params.delete('tryon');
      const rest = params.toString();
      history.replaceState(history.state, '', `${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`);
      requestAnimationFrame(() => this.open());
    }
  }

  /* ---------------- product/varianten ---------------- */

  get options() {
    return this.product.options || [];
  }

  idx(test) {
    return this.options.findIndex((o) => test(o.name));
  }

  variant(id) {
    return this.product.variants.find((v) => String(v.id) === String(id));
  }

  /** De variant die nu op de pagina gekozen is (productformulier), anders de eerste beschikbare. */
  pageVariant() {
    const input = document.querySelector('product-form-component form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] input[name="id"]');
    return this.variant(input?.value) || this.product.variants.find((v) => v.available) || this.product.variants[0];
  }

  /** Variant zoeken met dezelfde opties, behalve de opgegeven wijzigingen. */
  findVariant(base, changes) {
    const want = base.options.map((v, i) => (i in changes ? changes[i] : v));
    return (
      this.product.variants.find((v) => v.options.every((o, i) => o === want[i])) ||
      this.product.variants.find((v) => v.options.every((o, i) => isSize(this.options[i]?.name) || o === want[i]))
    );
  }

  /* ---------------- modal ---------------- */

  open() {
    track(this.endpoint, 'tryon_opened', { productId: this.product.id });
    if (!this.dialog) this.render();
    const saved = this.restore();
    const v = this.pageVariant();
    this.#state.variantId = this.#state.variantId || v.id;
    const sizeIdx = this.idx(isSize);
    const sizeChosen = !document.documentElement.classList.contains('orion-needs-size');
    if (!this.#state.size && sizeIdx >= 0 && sizeChosen) this.#state.size = v.options[sizeIdx];
    this.dialog.showModal();
    document.documentElement.classList.add('orion-tryon-open');
    if (saved?.generationId && saved.session) {
      this.#state.session = saved.session;
      this.#state.generationId = saved.generationId;
      this.#state.uploadId = saved.uploadId;
      this.#state.variantId = saved.variantId || this.#state.variantId;
      this.go('loading');
      this.pollStatus();
    } else {
      this.go(this.#state.photo ? 'review' : 'upload');
    }
  }

  close() {
    this.dialog?.close();
  }

  onClosed() {
    document.documentElement.classList.remove('orion-tryon-open');
    clearInterval(this.#msgTimer);
    clearTimeout(this.#preTimer);
  }

  render() {
    const d = document.createElement('dialog');
    d.className = 'orion-tryon-dialog';
    d.setAttribute('aria-labelledby', 'orion-tryon-title');
    d.innerHTML = `
      <div class="ot-sheet">
        <header class="ot-head">
          <div>
            <p class="ot-eyebrow">ORION</p>
            <h2 class="ot-title" id="orion-tryon-title">${esc(t.title)}</h2>
            <p class="ot-sub">${esc(t.subtitle)}</p>
          </div>
          <button type="button" class="ot-close" data-act="close" aria-label="${esc(t.close)}">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.4" fill="none"/></svg>
          </button>
        </header>
        <div class="ot-body" data-view></div>
        <p class="ot-live" role="status" aria-live="polite" data-live></p>
      </div>`;
    d.addEventListener('click', (e) => {
      if (e.target === d) this.close();
      const act = e.target.closest?.('[data-act]')?.dataset.act;
      if (act) this.action(act, e.target.closest('[data-act]'));
    });
    d.addEventListener('close', () => this.onClosed());
    d.addEventListener('change', (e) => this.onChange(e));
    document.body.append(d);
    this.dialog = d;
    this.view = d.querySelector('[data-view]');
    this.live = d.querySelector('[data-live]');
  }

  go(step, extra = {}) {
    this.#state.step = step;
    clearInterval(this.#msgTimer);
    clearInterval(this.#barTimer);
    clearTimeout(this.#preTimer);
    this.dialog.dataset.step = step;
    this.view.innerHTML = this[`view_${step}`](extra);
    this.view.scrollTop = 0;
    if (step === 'loading') {
      this.rotateMessages();
      this.startProgress();
    }
    if (step === 'result') this.prefetchColors();
  }

  /* ---------------- andere kleuren alvast maken ----------------
     Na de eerste look maakt de server de andere kleuren van hetzelfde stuk op de achtergrond (telt niet mee voor
     de limieten). Een klik op zo'n kleur geeft de klare look direct terug (de server herkent hem). */
  isReady(v, ci, color) {
    if (ci < 0 || color === v.options[ci]) return false;
    const target = this.findVariant(v, { [ci]: color });
    return !!target && (this.#pre.ready.has(String(target.id)) || this.#pre.seen.has(String(target.id)));
  }

  async prefetchColors() {
    const upload = this.#state.uploadId;
    if (!upload || !this.#state.generationId) return;
    if (this.#pre.upload !== upload) this.#pre = { upload, parent: null, ready: new Set(), seen: new Set(), busy: false, asked: false };
    if (!this.#pre.asked) {
      this.#pre.asked = true;
      try {
        const r = await this.api('/prefetch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ generationId: this.#state.generationId }),
        });
        if (r.queued > 0) {
          this.#pre.parent = this.#state.generationId;
          this.#pre.busy = true;
        }
      } catch {
        /* geen voorbereiding: kleuren gewoon op aanvraag */
      }
    }
    if (this.#pre.parent) this.pollPrefetch();
  }

  async pollPrefetch() {
    clearTimeout(this.#preTimer);
    if (this.#state.step !== 'result' || !this.#pre.parent) return;
    try {
      const { looks = [] } = await this.api(`/prefetch/${this.#pre.parent}`);
      for (const l of looks) if (l.status === 'completed') this.#pre.ready.add(String(l.variantId).split('/').pop());
      this.#pre.busy = looks.some((l) => l.status === 'queued' || l.status === 'processing');
    } catch {
      this.#pre.busy = false;
    }
    if (this.#state.step !== 'result') return;
    const v = this.variant(this.#state.variantId);
    const ci = this.idx(isColor);
    if (v) for (const b of this.view.querySelectorAll('[data-act="color"]')) b.classList.toggle('is-ready', this.isReady(v, ci, b.dataset.value));
    const note = this.view.querySelector('[data-pre-note]');
    if (note) note.textContent = this.#pre.busy ? t.prefetching : t.prefetchReady;
    if (this.#pre.busy) this.#preTimer = setTimeout(() => this.pollPrefetch(), 4000);
  }

  /* ---------------- voortgang in % ----------------
     Volgt de echte stappen van de server (rij -> voorbereiden -> AI maakt de look -> afwerken -> klaar);
     binnen de AI-stap loopt de balk vloeiend op richting ~90% en gaat nooit terug. */
  startProgress() {
    if (!this.#state.generationId) this.#prog = { shown: 0, stage: 'queued', stageAt: Date.now() };
    const tick = () => {
      const { stage, stageAt } = this.#prog;
      const secs = (Date.now() - stageAt) / 1000;
      let target;
      if (stage === 'done') target = 100;
      else if (stage === 'finishing') target = 92 + 6 * (1 - Math.exp(-secs / 4));
      else if (stage === 'creating') target = 12 + 78 * (1 - Math.exp(-secs / 20)); // ~40 s
      else if (stage === 'retrying') target = 70 + 22 * (1 - Math.exp(-secs / 25)); // een poging wordt vervangen: rustiger door else if (stage === 'preparing') target = 6 + 6 * (1 - Math.exp(-secs / 3));
      else target = 2 + 4 * (1 - Math.exp(-secs / 10)); // in de rij / sessie / upload
      const shown = Math.max(this.#prog.shown, Math.min(target, 100));
      this.#prog.shown = shown;
      const pct = Math.floor(shown);
      const bar = this.view.querySelector('[data-bar]');
      if (bar) bar.style.width = `${shown}%`;
      const label = this.view.querySelector('[data-pct]');
      if (label) label.textContent = `${pct}%`;
      this.view.querySelector('[data-meter]')?.setAttribute('aria-valuenow', String(pct));
    };
    tick();
    this.#barTimer = setInterval(tick, 200);
  }

  setProgress(stage) {
    if (stage !== this.#prog.stage) {
      this.#prog.stage = stage;
      this.#prog.stageAt = Date.now();
    }
  }

  productCard() {
    const v = this.variant(this.#state.variantId) || this.pageVariant();
    const label = v.options.filter((_, i) => !isSize(this.options[i]?.name)).join(' · ');
    return `
      <figure class="ot-card">
        <img src="${esc(v.image || this.product.image)}" alt="${esc(this.product.title)}" loading="lazy" width="600" height="900">
        <figcaption>
          <span class="ot-card__title">${esc(this.product.title)}</span>
          <span class="ot-card__meta">${esc(label)}</span>
          <span class="ot-card__price">${money(v.price)}</span>
        </figcaption>
      </figure>`;
  }

  view_upload() {
    return `
      <div class="ot-grid">
        <div class="ot-col">
          <h3 class="ot-h3">${esc(t.uploadTitle)}</h3>
          <p class="ot-text">${esc(t.uploadText)}</p>
          <label class="ot-drop">
            <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" data-file>
            <svg viewBox="0 0 48 48" aria-hidden="true"><rect x="7" y="12" width="34" height="26" rx="2" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="24" cy="25" r="7" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M17 12l3-4h8l3 4" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>
            <span class="ot-drop__title">${esc(t.choose)}</span>
            <span class="ot-drop__hint">${esc(t.chooseHint)}</span>
          </label>
          <ul class="ot-tips">${t.tips.map((x) => `<li>${esc(x)}</li>`).join('')}</ul>
        </div>
        <div class="ot-col ot-col--product">
          <p class="ot-label">${esc(t.selected)}</p>
          ${this.productCard()}
          ${this.pickers()}
        </div>
      </div>`;
  }

  /** Kleur en stuk kiezen vóór het maken van de look (alleen selecteren, nog niets genereren). */
  pickers() {
    const v = this.variant(this.#state.variantId) || this.pageVariant();
    const ci = this.idx(isColor);
    const pi = this.idx(isPiece);
    const colors = ci >= 0 ? this.options[ci].values : [];
    const pieces = pi >= 0 ? this.options[pi].values : [];
    return `
      ${colors.length > 1 ? `<div class="ot-pick"><p class="ot-label">${esc(t.pickColor)} · <span class="ot-pick__value">${esc(v.options[ci])}</span></p><div class="ot-swatches">${colors
        .map((c) => `<button type="button" class="ot-swatch${c === v.options[ci] ? ' is-on' : ''}" data-act="pick-color" data-value="${esc(c)}" style="--c:${COLOR_HEX[c.toLowerCase()] || '#ccc'}" aria-pressed="${c === v.options[ci]}"><span></span>${esc(c)}</button>`)
        .join('')}</div></div>` : ''}
      ${pieces.length > 1 ? `<div class="ot-pick"><p class="ot-label">${esc(t.pickPiece)}</p><div class="ot-chips">${pieces
        .map((x) => `<button type="button" class="ot-chip${x === v.options[pi] ? ' is-on' : ''}" data-act="pick-piece" data-value="${esc(x)}" aria-pressed="${x === v.options[pi]}">${esc(x)}</button>`)
        .join('')}</div></div>` : ''}`;
  }

  view_review() {
    return `
      <div class="ot-pair">
        <div>
          <p class="ot-label">${esc(t.yourPhoto)}</p>
          <figure class="ot-card ot-card--photo"><img src="${esc(this.#state.photoUrl)}" alt="${esc(t.yourPhoto)}"></figure>
          <label class="ot-textbtn"><input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" data-file hidden>${esc(t.changePhoto)}</label>
        </div>
        <div>
          <p class="ot-label">${esc(t.selected)}</p>
          ${this.productCard()}
        </div>
      </div>
      <div class="ot-review-pickers">${this.pickers()}</div>
      <div class="ot-consent">
        <p class="ot-note">${esc(t.privacy)} <a href="${esc(this.privacyUrl)}" target="_blank" rel="noopener">${esc(t.privacyLink)}</a></p>
        <label class="ot-check"><input type="checkbox" data-consent ${this.#state.consent ? 'checked' : ''}><span>${esc(t.consent)}</span></label>
      </div>
      <div class="ot-actions ot-actions--sticky">
        <button type="button" class="ot-btn ot-btn--primary" data-act="generate" ${this.#state.consent ? '' : 'disabled'}>${esc(t.generate)}</button>
      </div>`;
  }

  view_loading() {
    return `
      <div class="ot-loading">
        <div class="ot-loading__frame">
          ${this.#state.photoUrl ? `<img src="${esc(this.#state.photoUrl)}" alt="" aria-hidden="true">` : ''}
          <span class="ot-loading__shine" aria-hidden="true"></span>
        </div>
        <p class="ot-loading__msg" data-msg>${esc(t.loading[0])}</p>
        <p class="ot-loading__queue" data-queue></p>
        <div class="ot-meter" role="progressbar" aria-label="${esc(t.progress)}" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" data-meter>
          <div class="ot-progress"><span data-bar></span></div>
          <span class="ot-meter__pct" data-pct>0%</span>
        </div>
        <p class="ot-note">${esc(t.waitNote)}</p>
      </div>`;
  }

  view_result({ resultUrl, downloadUrl }) {
    const v = this.variant(this.#state.variantId);
    const ci = this.idx(isColor);
    const pi = this.idx(isPiece);
    const si = this.idx(isSize);
    const colors = ci >= 0 ? this.options[ci].values : [];
    const pieces = pi >= 0 ? this.options[pi].values : [];
    const sizes = si >= 0 ? this.product.variants.filter((x) => x.options.every((o, i) => i === si || o === v.options[i])) : [];
    const canShare = !!(navigator.share && navigator.canShare);
    return `
      <div class="ot-result">
        <figure class="ot-result__img">
          <img src="${esc(resultUrl)}" alt="${esc(t.resultTitle)}" data-result>
          <figcaption class="ot-note">${esc(t.visualNote)}</figcaption>
        </figure>
        <div class="ot-result__side">
          <h3 class="ot-h3 ot-h3--result">${esc(t.resultTitle)}</h3>
          ${this.productCard()}
          ${
            sizes.length
              ? `<fieldset class="ot-sizes"><legend>${esc(t.size)}</legend>${sizes
                  .map((x) => {
                    const sz = x.options[si];
                    return `<label class="ot-pill${x.available ? '' : ' is-off'}"><input type="radio" name="ot-size" value="${esc(sz)}" ${this.#state.size === sz ? 'checked' : ''} ${x.available ? '' : 'disabled'}><span>${esc(sz)}</span></label>`;
                  })
                  .join('')}</fieldset>`
              : ''
          }
          <div class="ot-actions ot-actions--sticky ot-actions--cart">
            <button type="button" class="ot-btn ot-btn--primary" data-act="add">${esc(this.addLabel())}</button>
          </div>
          ${
            colors.length > 1
              ? `<div class="ot-more"><p class="ot-label">${esc(t.anotherColor)}</p><div class="ot-swatches">${colors
                  .map((c) => `<button type="button" class="ot-swatch${c === v.options[ci] ? ' is-on' : ''}${this.isReady(v, ci, c) ? ' is-ready' : ''}" data-act="color" data-value="${esc(c)}" style="--c:${COLOR_HEX[c.toLowerCase()] || '#ccc'}" aria-label="${esc(c)}"><span></span>${esc(c)}</button>`)
                  .join('')}</div><p class="ot-note ot-pre-note" data-pre-note>${this.#pre.parent ? esc(this.#pre.busy ? t.prefetching : t.prefetchReady) : ''}</p></div>`
              : ''
          }
          ${
            pieces.length > 1
              ? `<div class="ot-more"><p class="ot-label">${esc(t.anotherPiece)}</p><div class="ot-chips">${pieces
                  .map((p) => `<button type="button" class="ot-chip${p === v.options[pi] ? ' is-on' : ''}" data-act="piece" data-value="${esc(p)}">${esc(p)}</button>`)
                  .join('')}</div></div>`
              : ''
          }
          <div class="ot-links">
            <button type="button" class="ot-textbtn" data-act="again">${esc(t.again)}</button>
            <a class="ot-textbtn" href="${esc(downloadUrl)}" data-act="download" download="orion-look.jpg">${esc(t.download)}</a>
            ${canShare ? `<button type="button" class="ot-textbtn" data-act="share">${esc(t.share)}</button>` : ''}
            <button type="button" class="ot-textbtn ot-textbtn--quiet" data-act="delete">${esc(t.deleteData)}</button>
          </div>
        </div>
      </div>`;
  }

  /** Variant die in de winkelwagen gaat: gekozen kleur/stuk + gekozen maat. */
  cartVariant() {
    const base = this.variant(this.#state.variantId);
    const si = this.idx(isSize);
    if (si < 0) return base;
    return this.#state.size ? this.findVariant(base, { [si]: this.#state.size }) : null;
  }

  addLabel() {
    const v = this.cartVariant();
    if (!v) return t.size;
    return v.available ? `${t.addToCart} · ${money(v.price)}` : t.soldOut;
  }

  view_error({ code }) {
    const key = ERROR_MAP[code] || 'GENERATION_FAILED';
    const pi = this.idx(isPiece);
    const v = this.variant(this.#state.variantId);
    const setValue = pi >= 0 ? this.options[pi].values.find((x) => /set/i.test(x)) : null;
    if (key === 'GENERATION_FAILED' && v && pi >= 0 && /bh|bra/i.test(v.options[pi]) && setValue) {
      return `
      <div class="ot-error">
        <p class="ot-error__text">${esc(t.braOnly)}</p>
        <div class="ot-actions">
          <button type="button" class="ot-btn ot-btn--primary" data-act="piece" data-value="${esc(setValue)}">${esc(t.trySet)}</button>
          <button type="button" class="ot-btn" data-act="retry">${esc(t.retry)}</button>
        </div>
      </div>`;
    }
    const photoIssue = ['NO_PERSON', 'MULTIPLE_PEOPLE', 'LOWER_BODY_NOT_VISIBLE', 'PHOTO_NOT_USABLE', 'CONTENT_REJECTED', 'PHOTO_EXPIRED'].includes(key);
    return `
      <div class="ot-error">
        <p class="ot-error__text">${esc(t.errors[key] || t.errors.GENERATION_FAILED)}</p>
        <div class="ot-actions">
          ${photoIssue
            ? `<label class="ot-btn ot-btn--primary"><input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" data-file hidden>${esc(t.otherPhoto)}</label>`
            : `<button type="button" class="ot-btn ot-btn--primary" data-act="retry">${esc(t.retry)}</button>`}
          ${key === 'LOWER_BODY_NOT_VISIBLE' ? `<button type="button" class="ot-btn" data-act="piece-top">${esc(t.anotherPiece)}</button>` : ''}
        </div>
      </div>`;
  }

  view_deleted() {
    return `<div class="ot-error"><p class="ot-error__text">${esc(t.deleted)}</p></div>`;
  }

  rotateMessages() {
    let i = 0;
    this.#msgTimer = setInterval(() => {
      i = (i + 1) % t.loading.length;
      const el = this.view.querySelector('[data-msg]');
      if (el) el.textContent = t.loading[i];
    }, 2800);
  }

  /* ---------------- gebeurtenissen ---------------- */

  async onChange(e) {
    const el = e.target;
    if (el.matches('[data-file]') && el.files?.[0]) {
      try {
        const blob = await prepareFile(el.files[0]);
        if (this.#state.photoUrl) URL.revokeObjectURL(this.#state.photoUrl);
        this.#state.photo = blob;
        this.#state.photoUrl = URL.createObjectURL(blob);
        this.#state.uploadId = null; // nieuwe foto = nieuwe upload (en nieuwe voorbereide kleuren)
        track(this.endpoint, 'tryon_photo_selected');
        this.go('review');
      } catch (err) {
        this.go('error', { code: err.message || 'PHOTO_NOT_USABLE' });
      }
    } else if (el.matches('[data-consent]')) {
      this.#state.consent = el.checked;
      this.view.querySelector('[data-act="generate"]')?.toggleAttribute('disabled', !el.checked);
      if (el.checked) {
        track(this.endpoint, 'tryon_consent_given');
        this.preUpload(); // foto alvast uploaden en voorbereiden: "Generate" start daarna meteen de AI
      } else if (this.#state.session) {
        this.withdraw(); // toestemming ingetrokken: al geüploade foto direct weg
      }
    } else if (el.name === 'ot-size') {
      this.#state.size = el.value;
      const add = this.view.querySelector('[data-act="add"]');
      if (add) add.textContent = this.addLabel();
      this.view.querySelector('.ot-sizes')?.classList.remove('is-attention');
    }
  }

  async action(act, el) {
    if (act === 'close') return this.close();
    if (act === 'generate' || act === 'retry' || act === 'again') {
      if (act === 'again') track(this.endpoint, 'tryon_generate_again');
      return this.generate(act === 'again'); // opnieuw = bewust een nieuwe look; anders komt een bewaarde look terug
    }
    if (act === 'color' || act === 'piece' || act === 'piece-top') {
      const v = this.variant(this.#state.variantId) || this.pageVariant();
      const i = act === 'color' ? this.idx(isColor) : this.idx(isPiece);
      let value = el.dataset.value;
      if (act === 'piece-top') {
        // bij een bovenlijffoto: een bovenstuk voorstellen (bh of bolero)
        value = (this.options[i]?.values || []).find((x) => /bh|bra|bolero|top/i.test(x));
        if (!value) return;
      }
      const next = this.findVariant(v, { [i]: value });
      if (!next) return;
      this.#state.variantId = next.id;
      track(this.endpoint, act === 'color' ? 'tryon_change_color' : 'tryon_try_other_product');
      return this.generate();
    }
    if (act === 'pick-color' || act === 'pick-piece') {
      const v = this.variant(this.#state.variantId) || this.pageVariant();
      const i = act === 'pick-color' ? this.idx(isColor) : this.idx(isPiece);
      const next = this.findVariant(v, { [i]: el.dataset.value });
      if (!next) return;
      this.#state.variantId = next.id;
      const scroll = this.view.scrollTop;
      this.go(this.#state.step);
      this.view.scrollTop = scroll; // op dezelfde plek blijven
      this.view.querySelector(`[data-act="${act}"][data-value="${CSS.escape(el.dataset.value)}"]`)?.focus();
      return;
    }
    if (act === 'add') return this.addToCart(el);
    if (act === 'download') track(this.endpoint, 'tryon_download');
    if (act === 'share') return this.share();
    if (act === 'delete') return this.deleteData();
  }

  /* ---------------- server ---------------- */

  async api(path, opts = {}) {
    const headers = { ...(opts.headers || {}) };
    if (this.#state.session) headers.Authorization = `Bearer ${this.#state.session.token}`;
    const res = await fetch(`${this.endpoint}/api/tryon${path}`, { ...opts, headers });
    const body = res.status === 204 ? {} : await res.json().catch(() => ({}));
    if (!res.ok) {
      const e = new Error(body.error || 'GENERATION_FAILED');
      e.status = res.status;
      throw e;
    }
    return body;
  }

  async ensureSession() {
    if (this.#state.session && this.#state.session.expiresAt > Date.now() + 60_000) return;
    this.#state.session = await this.api('/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ consent: true, policyVersion: this.policy, shop: location.hostname }),
    });
  }

  /** Na toestemming: sessie + upload + voorbereiding op de achtergrond (scheelt ~3-6 s na de klik). */
  preUpload() {
    if (this.#state.uploadId || this.#upload || !this.#state.photo) return;
    const photo = this.#state.photo;
    this.#upload = (async () => {
      await this.ensureSession();
      const fd = new FormData();
      fd.append('photo', photo, 'photo.jpg');
      const up = await this.api('/upload', { method: 'POST', body: fd });
      if (this.#state.photo === photo) this.#state.uploadId = up.uploadId; // intussen andere foto gekozen? dan niet gebruiken
    })().finally(() => {
      this.#upload = null;
    });
    this.#upload.catch(() => {}); // fouten komen bij de klik op "Generate" alsnog netjes naar voren
  }

  async withdraw() {
    const s = this.#state.session;
    this.#state.session = null;
    this.#state.uploadId = null;
    try {
      if (s) await fetch(`${this.endpoint}/api/tryon/session/${s.sessionId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${s.token}` } });
    } catch {
      /* sessie verloopt anders vanzelf */
    }
  }

  async generate(fresh = false) {
    if (this.#state.busy) return; // geen dubbele generaties bij dubbelklikken
    this.#state.busy = true;
    try {
      if (!this.#state.consent) return this.go('review');
      this.#prog = { shown: 0, stage: 'queued', stageAt: Date.now() };
      this.go('loading');
      if (this.#upload) await this.#upload.catch(() => {}); // voorbereide upload afwachten
      await this.ensureSession();
      if (!this.#state.uploadId) {
        if (!this.#state.photo) throw new Error('PHOTO_EXPIRED');
        const fd = new FormData();
        fd.append('photo', this.#state.photo, 'photo.jpg');
        const up = await this.api('/upload', { method: 'POST', body: fd });
        this.#state.uploadId = up.uploadId;
      }
      const v = this.variant(this.#state.variantId) || this.pageVariant();
      track(this.endpoint, 'tryon_generation_started', { productId: this.product.id, variantId: v.id });
      const g = await this.api('/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uploadId: this.#state.uploadId, products: [{ productId: String(this.product.id), variantId: String(v.id) }], ...(fresh ? { fresh: true } : {}) }),
      });
      this.#state.generationId = g.generationId;
      this.save();
      this.pollStatus();
    } catch (err) {
      this.fail(err.message);
      if (err.message === 'SESSION_EXPIRED') this.#state.session = null;
      if (['PHOTO_EXPIRED', 'SESSION_EXPIRED', 'EXPIRED'].includes(err.message)) this.#state.uploadId = null;
    } finally {
      this.#state.busy = false;
    }
  }

  pollStatus() {
    clearTimeout(this.#poll);
    const started = Date.now();
    const tick = async () => {
      try {
        const s = await this.api(`/status/${this.#state.generationId}`);
        const q = this.view.querySelector('[data-queue]');
        if (q) q.textContent = s.status === 'queued' && s.position > 1 ? t.queue(s.position - 1) : '';
        if (s.status === 'processing') this.setProgress(s.stage || 'preparing');
        if (s.status === 'completed' && s.resultUrl) {
          this.clearSaved();
          track(this.endpoint, 'tryon_generation_completed', { productId: this.product.id });
          await new Promise((r) => {
            const img = new Image();
            img.onload = img.onerror = r;
            img.src = s.resultUrl; // eerst laden, dan pas tonen (geen half beeld)
          });
          this.#state.result = s;
          if (this.#pre.upload !== this.#state.uploadId) this.#pre = { upload: this.#state.uploadId, parent: null, ready: new Set(), seen: new Set(), busy: false, asked: false };
          this.#pre.seen.add(String(this.#state.variantId)); // deze kleur is gemaakt: terugklikken = direct
          this.setProgress('done');
          await new Promise((r) => setTimeout(r, 450)); // 100% even laten zien
          this.live.textContent = t.resultTitle;
          return this.go('result', s);
        }
        if (s.status === 'failed' || s.status === 'expired') {
          this.clearSaved();
          return this.fail(s.errorCode || 'GENERATION_FAILED');
        }
        if (Date.now() - started > 8 * 60_000) return this.fail('GENERATION_FAILED');
        this.#poll = setTimeout(tick, 2500);
      } catch (err) {
        if (err.status === 401 || err.status === 404) {
          this.clearSaved();
          return this.fail('PHOTO_EXPIRED');
        }
        this.#poll = setTimeout(tick, 4000); // kort netwerkprobleem: gewoon opnieuw proberen
      }
    };
    tick();
  }

  fail(code) {
    track(this.endpoint, 'tryon_generation_failed', { code: ERROR_MAP[code] || 'GENERATION_FAILED' });
    this.go('error', { code });
  }

  async addToCart(button) {
    const v = this.cartVariant();
    if (!v) {
      // eerst een maat kiezen: naar de maten scrollen en ze even laten oplichten
      const sizes = this.view.querySelector('.ot-sizes');
      sizes?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      sizes?.classList.remove('is-attention');
      void sizes?.offsetWidth;
      sizes?.classList.add('is-attention');
      return;
    }
    if (!v.available) {
      button.textContent = t.soldOut;
      return;
    }
    button.disabled = true;
    button.textContent = t.adding;
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
    if (!sectionIds.includes('cart-drawer-section')) sectionIds.push('cart-drawer-section');
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(v.id), quantity: 1 }], promise: deferred.promise }));
    try {
      const routes = window.Theme?.routes || {};
      // markeren dat deze bestelling via de try-on kwam (voor conversiemeting try-on vs. normaal)
      await fetch(`${routes.cart_update_url || '/cart/update.js'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { orion_tryon: 'ja' } }),
      });
      const res = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(v.id), quantity: 1, properties: { _orion_bron: 'try-on' } }], sections: [...new Set(sectionIds)].join(','), sections_url: location.pathname }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error('ADD_FAILED');
      const cart = await fetch(`${routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'orion-tryon', sections: data.sections, didError: false } });
      track(this.endpoint, 'tryon_add_to_cart', { productId: this.product.id, variantId: v.id });
      button.textContent = t.added;
      setTimeout(() => this.close(), 600);
    } catch (err) {
      deferred.reject(err);
      button.disabled = false;
      button.textContent = this.addLabel();
    }
  }

  async share() {
    try {
      const img = this.view.querySelector('[data-result]');
      const blob = await fetch(img.src).then((r) => r.blob());
      const file = new File([blob], 'orion-look.jpg', { type: 'image/jpeg' });
      if (navigator.canShare({ files: [file] })) await navigator.share({ files: [file], title: 'ORION', text: t.subtitle });
    } catch {
      /* delen geannuleerd */
    }
  }

  async deleteData() {
    try {
      if (this.#state.session) await this.api(`/session/${this.#state.session.sessionId}`, { method: 'DELETE' });
    } catch {
      /* sessie was al weg */
    }
    if (this.#state.photoUrl) URL.revokeObjectURL(this.#state.photoUrl);
    this.#state = { ...this.#state, photo: null, photoUrl: null, session: null, uploadId: null, generationId: null, consent: false };
    this.#pre = { upload: null, parent: null, ready: new Set(), seen: new Set(), busy: false, asked: false };
    this.clearSaved();
    this.go('deleted');
  }

  /* ---------------- herladen tijdens het maken ---------------- */

  save() {
    try {
      const { session, generationId, uploadId, variantId } = this.#state;
      sessionStorage.setItem(STORE_KEY, JSON.stringify({ session, generationId, uploadId, variantId, productId: this.product.id }));
    } catch {
      /* privémodus */
    }
  }

  restore() {
    try {
      const s = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
      return s && String(s.productId) === String(this.product.id) ? s : null;
    } catch {
      return null;
    }
  }

  clearSaved() {
    try {
      sessionStorage.removeItem(STORE_KEY);
    } catch {
      /* niets */
    }
  }
}

if (!customElements.get('orion-tryon')) customElements.define('orion-tryon', OrionTryOn);
